import { useMemo, useState } from "react";
import { Plus, X } from "lucide-react";
import { PERSON_PROFICIENCY_LEVELS, type PersonProficiencyLevel } from "@releve/shared";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { NumberInput } from "@/components/ui/number-input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useSkills } from "@/hooks/useSkills";
import { useTranslation } from "@/hooks/useTranslation";
import { formatLocalISODate, parseLocalISODate } from "@/lib/dateUtils";
import type { Skill } from "@/types/people";
import { CreateSkillDialog } from "./CreateSkillDialog";
import { SectionHead } from "./person-form/fields";
import type { SkillDraft } from "./person-form/personFormSchema";
import { skillCategoryLabel } from "./person-detail-grids/personDisciplineGridColumns";

interface SkillSelectorProps {
  value: SkillDraft[];
  onChange: (skills: SkillDraft[]) => void;
  organizationId?: string;
}

const draftFromSkill = (skill: Skill): SkillDraft => ({
  skill_id: skill.id,
  name: skill.name,
  category: skill.category ?? "",
  proficiency_level: "intermediate",
  years_experience: null,
  last_used: formatLocalISODate(new Date()),
  notes: "",
});

/**
 * The person form's skills: pick from the org vocabulary, create a new skill
 * (`CreateSkillDialog`), and edit each assignment's proficiency, experience,
 * last-used day and notes in a popover. A skill is assigned at most once
 * (the `(person_id, skill_id)` unique key).
 */
export function SkillSelector({ value, onChange, organizationId }: SkillSelectorProps) {
  const { t } = useTranslation("people");
  const { data: skills = [] } = useSkills(organizationId);
  const [createOpen, setCreateOpen] = useState(false);

  const assigned = useMemo(() => new Set(value.map((s) => s.skill_id)), [value]);
  const available = skills.filter((skill) => !assigned.has(skill.id));

  const add = (draft: SkillDraft) => {
    if (assigned.has(draft.skill_id)) return;
    onChange([...value, draft]);
  };
  const update = (skillId: string, patch: Partial<SkillDraft>) =>
    onChange(value.map((s) => (s.skill_id === skillId ? { ...s, ...patch } : s)));
  const remove = (skillId: string) => onChange(value.filter((s) => s.skill_id !== skillId));

  const nameOf = (draft: SkillDraft) => skills.find((s) => s.id === draft.skill_id)?.name ?? draft.name;

  // "Popular" suggestions: up to five unassigned skills from each of the
  // first three categories, shown while the person has none.
  const suggestions = useMemo(() => {
    const byCategory = new Map<string, Skill[]>();
    for (const skill of available) {
      const key = skill.category ?? "";
      byCategory.set(key, [...(byCategory.get(key) ?? []), skill]);
    }
    return [...byCategory.entries()].slice(0, 3).map(([category, list]) => ({ category, list: list.slice(0, 5) }));
  }, [available]);

  return (
    <section>
      <SectionHead>{t("form.sections.skills")}</SectionHead>
      <div className="flex gap-2">
        <Select
          value=""
          onValueChange={(skillId) => {
            const skill = skills.find((s) => s.id === skillId);
            if (skill) add(draftFromSkill(skill));
          }}
        >
          <SelectTrigger className="flex-1" aria-label={t("form.skill.pick")}>
            <SelectValue placeholder={t("form.skill.pick")} />
          </SelectTrigger>
          <SelectContent>
            {available.map((skill) => (
              <SelectItem key={skill.id} value={skill.id}>
                {skill.category ? `${skill.name} · ${skillCategoryLabel(t, skill.category)}` : skill.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="button" variant="outline" onClick={() => setCreateOpen(true)} disabled={!organizationId}>
          <Plus className="h-4 w-4 mr-2" />
          {t("form.skill.new")}
        </Button>
      </div>

      {value.length > 0 ? (
        <ul className="mt-3 flex flex-wrap gap-2" aria-label={t("form.skill.assigned")}>
          {value.map((draft) => (
            <li key={draft.skill_id} className="flex items-center">
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" size="sm" className="rounded-r-none">
                    {nameOf(draft)}
                    {draft.proficiency_level && (
                      <span className="ml-2 text-muted-foreground">
                        {t(`common:people.proficiency.${draft.proficiency_level}`)}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80" align="start">
                  <div className="space-y-3">
                    <p className="font-medium leading-none">{nameOf(draft)}</p>
                    <div className="space-y-1">
                      <Label htmlFor={`skill-proficiency-${draft.skill_id}`}>{t("form.skill.proficiency")}</Label>
                      <Select
                        value={draft.proficiency_level ?? ""}
                        onValueChange={(level) =>
                          update(draft.skill_id, { proficiency_level: level as PersonProficiencyLevel })
                        }
                      >
                        <SelectTrigger id={`skill-proficiency-${draft.skill_id}`}>
                          <SelectValue placeholder={t("form.skill.proficiencyPlaceholder")} />
                        </SelectTrigger>
                        <SelectContent>
                          {PERSON_PROFICIENCY_LEVELS.map((level) => (
                            <SelectItem key={level} value={level}>
                              {t(`common:people.proficiency.${level}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label htmlFor={`skill-years-${draft.skill_id}`}>{t("form.skill.years")}</Label>
                        <NumberInput
                          id={`skill-years-${draft.skill_id}`}
                          min={0}
                          max={50}
                          step={1}
                          allowEmpty
                          value={draft.years_experience ?? ""}
                          onChange={(years) =>
                            update(draft.skill_id, { years_experience: years === "" ? null : Number(years) })
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        {/* Wrapping label: DatePicker takes no id. */}
                        <Label className="block space-y-1">
                          <span>{t("form.skill.lastUsed")}</span>
                          <DatePicker
                            value={parseLocalISODate(draft.last_used)}
                            onChange={(date) => update(draft.skill_id, { last_used: formatLocalISODate(date) })}
                          />
                        </Label>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`skill-notes-${draft.skill_id}`}>{t("form.skill.notes")}</Label>
                      <Textarea
                        id={`skill-notes-${draft.skill_id}`}
                        rows={2}
                        value={draft.notes}
                        onChange={(e) => update(draft.skill_id, { notes: e.target.value })}
                      />
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-l-none border-l-0 px-2"
                aria-label={t("form.skill.remove", { name: nameOf(draft) })}
                onClick={() => remove(draft.skill_id)}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-3 space-y-3">
          <p className="text-sm text-muted-foreground">{t("form.skill.empty")}</p>
          {suggestions.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">{t("form.skill.popular")}</p>
              {suggestions.map(({ category, list }) => (
                <div key={category || "none"} className="space-y-1">
                  {category && <p className="text-xs text-muted-foreground">{skillCategoryLabel(t, category)}</p>}
                  <div className="flex flex-wrap gap-1">
                    {list.map((skill) => (
                      <Button
                        key={skill.id}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => add(draftFromSkill(skill))}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        {skill.name}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {createOpen && organizationId && (
        <CreateSkillDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          organizationId={organizationId}
          skills={skills}
          onCreated={add}
        />
      )}
    </section>
  );
}
